import {
  Box,
  Select,
  Divider,
  BoxProps,
  MenuItem,
  IconButton,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import { Icon } from "../Icon";
import { forwardRef } from "react";
import { COLOR_CONSTANTS } from "../../constants/styles";
import { truncateNumber } from "../../utils/truncateNumber";

/**
 * 지도 좌표 표시 바 props — x/y/z + 좌표계 선택 + GPS 링크 토글.
 *
 * 지원 좌표계: Korea2000 / Google Mercator / WGS84 / UTM.
 * WGS84 는 6자리 소수점, 나머지는 2자리.
 */
export interface MapCoordinateInfoProps extends BoxProps {
  x?: number;
  y?: number;
  z?: number;
  /** GPS 링크 활성화 여부 */
  linked?: boolean;
  onLinkClick?: () => void;
  /** 현재 좌표계 명칭 */
  coordinateSystem?: string;
  onCoordinateSystemChange?: (event: SelectChangeEvent<string>) => void;
}

export const MapCoordinateInfo = forwardRef<
  HTMLDivElement,
  MapCoordinateInfoProps
>(
  (
    {
      x,
      y,
      z,
      linked = false,
      onLinkClick,
      coordinateSystem = "Korea2000",
      onCoordinateSystemChange,
      ...props
    },
    ref
  ) => {
    return (
      <Box ref={ref} sx={container} {...props}>
        <IconButton sx={iconButton} onClick={onLinkClick}>
          <Icon
            size={16}
            color={linked ? COLOR_CONSTANTS.primary.main : "#ffffff"}
            name={linked ? "etc/GpsFixed" : "etc/GpsNotFixed"}
          />
        </IconButton>
        <Select
          size="small"
          sx={selectBox}
          value={coordinateSystem}
          onChange={onCoordinateSystemChange}
          IconComponent={() => (
            <Icon name="16/arrow/bottom" size={16} sx={arrowIcon} />
          )}
          MenuProps={{
            anchorOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            PaperProps: {
              sx: menuPaper,
            },
            disableAutoFocus: true,
            disableEnforceFocus: true,
            disableRestoreFocus: true,
          }}
        >
          <MenuItem value="Korea2000" sx={menuItem}>
            Korea2000
          </MenuItem>
          <MenuItem value="Google Mercator" sx={menuItem}>
            Google Mercator
          </MenuItem>
          <MenuItem value="WGS84" sx={menuItem}>
            WGS84
          </MenuItem>
          <MenuItem value="UTM" sx={menuItem}>
            UTM
          </MenuItem>
        </Select>
        <Box sx={coordinateSection}>
          <Typography sx={coordinateText}>
            x:{" "}
            {x !== undefined
              ? truncateNumber(x, coordinateSystem === "WGS84" ? 6 : 2)
              : "-"}
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Typography sx={coordinateText}>
            y:{" "}
            {y !== undefined
              ? truncateNumber(y, coordinateSystem === "WGS84" ? 6 : 2)
              : "-"}
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Typography sx={coordinateText}>
            z:{" "}
            {z !== undefined
              ? truncateNumber(z, coordinateSystem === "WGS84" ? 6 : 2)
              : "-"}
          </Typography>
        </Box>
      </Box>
    );
  }
);

MapCoordinateInfo.displayName = "MapCoordinateInfo";

const container = {
  width: 472,
  height: 24,
  display: "flex",
  alignItems: "center",
  gap: 1,
  borderRadius: "4px",
  padding: "4px 8px",
  opacity: 0.8,
} as const;

const iconButton = {
  width: 24,
  height: 24,
  borderRadius: "4px",
  padding: 0,
  minWidth: "auto",
  backgroundColor: COLOR_CONSTANTS.COLOR.GREY[600],
  "&:hover": {
    backgroundColor: COLOR_CONSTANTS.COLOR.GREY[500],
  },
} as const;

const selectBox = {
  width: 120,
  minWidth: 120,
  height: 24,
  padding: "0 8px",
  backgroundColor: COLOR_CONSTANTS.COLOR.GREY[600],
  color: "#FFFFFF",
  fontSize: "12px",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "2px 8px !important",
    fontSize: "12px",
    lineHeight: "20px",
  },
  "& .MuiSvgIcon-root": {
    display: "none",
  },
} as const;

const menuPaper = {
  marginTop: "-8px",
  borderRadius: "4px",
  backgroundColor: COLOR_CONSTANTS.COLOR.GREY[600],
} as const;

const menuItem = {
  color: "#fff",
  fontSize: "12px",
  padding: "4px 12px",
  minHeight: "auto",

  "&.Mui-selected": {
    backgroundColor: COLOR_CONSTANTS.COLOR.GREY[800],
    color: "#fff",
    "&:hover": {
      backgroundColor: COLOR_CONSTANTS.COLOR.GREY[700],
    },
    "&:focus": {
      backgroundColor: COLOR_CONSTANTS.COLOR.GREY[800],
    },
  },
} as const;

const coordinateSection = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  backgroundColor: COLOR_CONSTANTS.COLOR.GREY[600],
  borderRadius: "4px",
  padding: "2px 8px",
  gap: 1,
} as const;

const arrowIcon = {
  "& path": {
    stroke: "#ffffff",
    strokeOpacity: 1,
  },
} as const;

const coordinateText = {
  width: 105,
  maxWidth: 105,
  color: "#FFFFFF",
  fontSize: "12px",
  lineHeight: "18px",
  fontWeight: 400,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    overflow: "auto",
    textOverflow: "unset",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
} as const;
